/* eslint-disable */
import { USER_SERVICE_PRESENCE_STATUS_PRESENT } from '../../../../Constants/User/User.constants';
import throwAuthCouldNotRefreshAccessTokenWhilstNotLoggedInError from '../../../../Errors/Auth/AuthCouldNotRefreshAccessTokenWhilstNotLoggedIn.error';
import AuthAdapter from '../Auth.adapter';

/**
 * AuthBoralSecurityAdapter
 * @class AuthBoralSecurityAdapter
 * @implements {AuthServiceAdapterContract}
 * @extends {AuthAdapter}
 */
class AuthBoralSecurityAdapter extends AuthAdapter {
  /**
   * The AuthBoralSecurityAdapter specific config
   * @type {AuthBoralSecurityAdapterConfig}
   * @memberof AuthBoralSecurityAdapter
   */
  _config;

  /**
   * How long the _token_'s should live
   * @type {number} The amount of time in milliseconds
   * @memberof AuthBoralSecurityAdapter
   */
  _tokenDuration = 43200000; // 12 hours

  /**
   * The `HttpService`
   * @type {HttpService}
   * @memberof AuthBoralSecurityAdapter
   */
  httpService;

  /**
   * Creates an instance of AuthBoralSecurityAdapter
   * @param {AuthBoralSecurityAdapterCreateDto} dto The params required to create an instance of `AuthBoralSecurityAdapter`
   * @memberof AuthBoralSecurityAdapter
   */
  constructor({ config, httpService, storageService }) {
    super({ storageService });
    this._config = config;
    this.httpService = httpService;
  }

  /**
   * Create an `accessToken` from a `username` and a `password`
   * @param {AuthServiceLoginWithUsernameAndPasswordDto} dto The required params to login
   * @returns {string} The access token
   * @memberof AuthBoralSecurityAdapter
   */
  _createAccessTokenFromUsernameAndPassword = async ({ password, username }) => {
    const tokenDuration = this._getTokenDuration();
    /** @type {{ token: string }} */
    const { token } = await this.httpService.post(this._config.authorizeUrl, {
      data: {
        aud: `biz-order-service`,
        exp: String(tokenDuration),
        iss: `boral`,
        password,
        service: `STSService`,
        username,
      },
    });
    return token;
  };

  /**
   * Create an `accessToken` from a `refreshToken`
   * @returns {string} The access token
   * @memberof AuthBoralSecurityAdapter
   */
  _createAccessTokenFromRefreshToken = async () => {
    if (!this.isAuthenticated()) {
      return throwAuthCouldNotRefreshAccessTokenWhilstNotLoggedInError();
    }
    // Currently, `refreshToken`'s are not directly supported, so instead, we use the existing `accessToken`
    const headers = this.createHeaders();
    const tokenDuration = this._getTokenDuration();
    /** @type {{ token: string }} */
    const { token } = await this.httpService.post(`${this._config.authorizeUrl}/refresh`, {
      data: {
        exp: String(tokenDuration),
      },
      headers,
    });
    return token;
  };

  /**
   * Convert Boral's _token_ into a known `token`
   * @param {string} token Boral's own _token_
   * @returns {AuthEntityTokenAccess} The transformed `token`
   * @memberof AuthBoralSecurityAdapter
   */
  _toTokenAccess = (token) => {
    const tokenDuration = this._getTokenDuration();
    const expiresAt = Date.now() + tokenDuration;
    return {
      authorizeUrl: this._config.authorizeUrl,
      claims: {},
      expiresAt,
      roles: [],
      scopes: [],
      token,
      type: `Bearer`,
      userinfoUrl: this._config.authorizeUrl,
    };
  };

  /**
   * Create a `UserEntity` out of an `username`
   * @param {string} username The user's `username`
   * @returns {UserEntity} The transformed user
   * @memberof AuthBoralAdapter
   */
  _toUser = (username) => {
    const displayName = username.split(`@`)[0];
    const initials = displayName.substr(0, 2).toUpperCase();
    return {
      displayName,
      id: username,
      initials,
      login: username,
      presence: USER_SERVICE_PRESENCE_STATUS_PRESENT,
      username,
    };
  };

  /**
   * Clears auth from storage. Does not log out the user.
   * @returns {void} void
   * @memberof AuthService
   */
  clearAuth = () => {
    return;
  };

  /**
   * Attempt to login a user with an `username` and a `password`
   * @param {AuthServiceLoginWithUsernameAndPasswordDto} dto The required params to login
   * @returns {Promise<AuthEntity>} The auth entity
   * @memberof AuthBoralSecurityAdapter
   */
  loginWithUsernameAndPassword = async ({ password, username }) => {
    const token = await this._createAccessTokenFromUsernameAndPassword({ password, username });
    const accessToken = this._toTokenAccess(token);
    /** @type {AuthEntity} */
    const newAuth = {
      accessToken,
      id: accessToken.token,
      idToken: null,
      sessionToken: null,
      user: this._toUser(username),
    };
    return this._changeState(newAuth);
  };

  /**
   * Logout a user
   * @returns {Promise<null>} The updated representation of the current user
   * @memberof AuthBoralSecurityAdapter
   */
  logout = async () => {
    const newAuth = null;
    return this._changeState(newAuth);
  };

  /**
   * Refresh auth for a user
   * @returns {Promise<AuthEntity>} The updated representation of the current user
   * @memberof AuthBoralSecurityAdapter
   */
  refreshAccessToken = async () => {
    const token = await this._createAccessTokenFromRefreshToken();
    const accessToken = this._toTokenAccess(token);
    /** @type {AuthEntity} */
    const newAuth = {
      ...this.getAuth(),
      accessToken,
      id: accessToken.token,
    };
    return this._changeState(newAuth);
  };
}

export default AuthBoralSecurityAdapter;
